import {
  Avatar,
  Box,
  Center,
  Checkbox,
  Group,
  MultiSelect,
  SelectItemProps,
  SegmentedControl,
  Space,
  Stack,
  Text as MantineText,
  ThemeIcon,
  Tooltip,
} from '@mantine/core';
import { IconCloudDownload, IconUser } from '@tabler/icons';
import { forwardRef, useContext, useEffect, useMemo, useState } from 'react';
import { ThemeText, ThemeTitle } from '../../../Atoms/ThemeText';
import CreateEventIcon from '../../../Organismns/Eventlist/CreateEventIcon';
import EventSearchAutocomplete from '../../../Organismns/Event/EventSearchAutocompleteV2';
import IntroductionTour from '../../../Organismns/Tour/IntroductionTour';
import { useJwtClaims } from '../../../Service/Context/AuthContext';
import { useBookitupScope } from '../../../Service/Context/BookitupSubscriptionContext';
import { EventsContext } from '../../../Service/Context/EventsContext';
import { ProfileContext } from '../../../Service/Context/ProfileContext';
import { ViolationContext } from '../../../Service/Context/ViolationContext';
import { WindowContext } from '../../../Service/Context/WindowContext';
import { DataS } from '../../../Service/DataS';
import { EventS } from '../../../Service/EventS';
import { LicenceS } from '../../../Service/LicenceS';
import { ProfileShare } from '../../../Service/ProfileS';
import { fetchRecommendableContacts } from '../../../Service/restapi/contactGroupService';
import Contact from '../../../Types/Contact';
import { openExportEventDatesModal } from '../../Event/EventDateModals';
import BookingStateItem from '../BookingStateItem';
import AvailabilitySelection from './AvailabilitySelection';
import DateIntervalSelection from './DateIntervalSelection';
import ExternalCalendarsSelection from './ExternalCalendarsSelection';
import EventKindSelection from './EventKindSelection';
import ListBookingStateSelection from './ListBookingStateSelection';

interface ApplicationUserFilterItemProps extends SelectItemProps {
  email: string;
  firstName?: string;
  lastName?: string;
}

type ApplicationUserLabelData = {
  email: string;
  firstName?: string;
  lastName?: string;
};

const getApplicationUserDisplayName = ({
  firstName,
  lastName,
}: Pick<ApplicationUserLabelData, 'firstName' | 'lastName'>) => [firstName, lastName].filter(Boolean).join(' ').trim();

const getApplicationUserInitials = ({ firstName, lastName, email }: ApplicationUserLabelData) => {
  const initials = `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`;
  return initials || email.charAt(0).toUpperCase();
};

const ApplicationUserFilterItem = forwardRef<HTMLDivElement, ApplicationUserFilterItemProps>(
  ({ label, email, firstName, lastName, ...rest }: ApplicationUserFilterItemProps, ref) => (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <div ref={ref} {...rest}>
      <Group noWrap spacing="sm">
        <Avatar size={30} radius="xl">
          {getApplicationUserInitials({ firstName, lastName, email })}
        </Avatar>
        <Stack spacing={0}>
          <MantineText size="sm" weight={500}>
            {label}
          </MantineText>
          {label !== email && (
            <MantineText size="xs" color="dimmed">
              {email}
            </MantineText>
          )}
        </Stack>
      </Group>
    </div>
  ),
);

const EventListNavBar = () => {
  const { sm, windowHeight } = useContext(WindowContext);
  const { licence } = useJwtClaims();
  const { isScopeSelected: hasGeneralEventsScope } = useBookitupScope('GENERAL_EVENTS');
  const { isScopeSelected: hasSettingsScope } = useBookitupScope('SETTINGS');
  const { executeWithLicenceCheck } = useContext(ViolationContext);
  const { filter, updateFilter, view, setView, query, setQuery } = useContext(EventsContext);
  const {
    agencyModeActive,
    sorting,
    lastContactShare,
    filterCustomerContactShared,
    recommendationContacts,
    showAppointments,
    applicationUsersOfInterests = [],
  } = filter;
  const [contacts, setContacts] = useState<Contact[]>([]);
  const contactOptions: any = [...DataS.getContactsOptions(contacts)];
  const { profile, bookitupTeam } = useContext<any>(ProfileContext);
  const applicationUsers: ProfileShare[] = bookitupTeam.shares;
  const loadingApplicationUsers = bookitupTeam.loading && !bookitupTeam.sharesLoaded;
  const [showIntroductionTour, setShowIntroductionTour] = useState<boolean>(false);
  const [datelessEventCount, setDatelessEventCount] = useState(0);
  const showLicenceHeader = licence && (licence === 'BASIC' || licence === 'INTERACTIVE_TRIAL_30DAYS');

  const [futureEventCount, setFutureEventCount] = useState(0);

  useEffect(() => {
    EventS.fetchFutureEventsCount().then(setFutureEventCount);
  }, []);

  useEffect(() => {
    if (!hasGeneralEventsScope) {
      return;
    }

    bookitupTeam.ensureSharesLoaded().catch(() => undefined);
  }, [bookitupTeam, hasGeneralEventsScope]);

  useEffect(() => {
    EventS.fetchDatelessEventsCount().then(setDatelessEventCount);
    fetchRecommendableContacts().then((resp) => {
      if (resp.ok) {
        resp.json().then((fetchedContacts) => {
          setContacts(fetchedContacts);
        });
      }
    });
  }, []);

  const applicationUserItems = useMemo(
    () =>
      applicationUsers.map((applicationUser) => ({
        value: applicationUser.applicationUserId.toString(),
        label: getApplicationUserDisplayName(applicationUser) || applicationUser.email,
        email: applicationUser.email,
        firstName: applicationUser.firstName,
        lastName: applicationUser.lastName,
      })),
    [applicationUsers],
  );

  const onApplicationUsersChange = (values: string[]) => {
    updateFilter({ applicationUsersOfInterests: values.map(Number), page: 0 });
  };

  return (
    <div>
      {showIntroductionTour && <IntroductionTour />}
      <div
        style={{
          height: windowHeight - (sm ? 140 : 80 + (showLicenceHeader ? 40 : 0)),
          overflow: 'auto',
        }}
      >
        {!sm && (
          <Group p="sm" position="apart" sx={{ alignItems: 'center' }}>
            <ThemeTitle order={3}>Events</ThemeTitle>
            <Group spacing="xs">
              <Tooltip label="Export als CSV" transition="slide-down">
                <ThemeIcon
                  size={20}
                  style={{ cursor: 'pointer' }}
                  variant="gradient"
                  gradient={{ from: '#647c91', to: '#a2a7b0', deg: 135 }}
                  sx={{ marginRight: 3 }}
                >
                  <IconCloudDownload color="white" onClick={openExportEventDatesModal} />
                </ThemeIcon>
              </Tooltip>
              <CreateEventIcon onClickCallback={() => setShowIntroductionTour(false)} />
            </Group>
          </Group>
        )}
        {view === 'list' && sorting !== 'createdAt' && (
          <Box p="xs" mt={5}>
            <EventSearchAutocomplete />
          </Box>
        )}
        {agencyModeActive && view === 'list' ? (
          <>
            <Box p="xs">
              <MultiSelect
                multiple
                variant="filled"
                searchable
                clearable
                placeholder="Dienstleister filtern"
                value={recommendationContacts}
                data={contactOptions}
                onChange={(val) => updateFilter({ recommendationContacts: val })}
              />
            </Box>
            <Space h="md" />
            <ThemeText pl={10}>Auftragsdaten</ThemeText>
            <Space h="xs" />
            <BookingStateItem
              label="Alle"
              onClick={() => {
                updateFilter({
                  filterCustomerContactShared: !filterCustomerContactShared,
                });
                updateFilter({ lastContactShare: null });
              }}
              active={!filterCustomerContactShared}
            />
            <BookingStateItem
              label="Nur übermittelte"
              onClick={() => {
                updateFilter({ lastContactShare: true });
                updateFilter({ filterCustomerContactShared: true });
              }}
              active={lastContactShare === true}
            />
            <BookingStateItem
              label="Nur nicht übermittelte"
              onClick={() => {
                updateFilter({ lastContactShare: false });
                updateFilter({ filterCustomerContactShared: true });
              }}
              active={lastContactShare === false}
            />
          </>
        ) : (
          <>
            {profile && LicenceS.hasAccessToResourceModule(profile.id) && (
              <>
                <ThemeText mt="md" mb="xs" pl={10}>
                  Ansicht
                </ThemeText>
                <BookingStateItem label="Liste" onClick={() => setView('list')} active={view === 'list'} />
                <BookingStateItem label="Kalender" active={view === 'calendar'} onClick={() => setView('calendar')} />
                <BookingStateItem
                  label="Zeitleiste"
                  onClick={() =>
                    executeWithLicenceCheck(LicenceS.Restrictions.RESOURCE_PLANNING, () => setView('timeline'))
                  }
                  active={view === 'timeline'}
                />
              </>
            )}

            {view === 'list' && (
              <>
                <Space h="md" />
                <ThemeText pl={10}>Sortieren</ThemeText>
                <Space h="xs" />
                <BookingStateItem
                  label="Chronologisch"
                  onClick={() => updateFilter({ sorting: 'chronological' })}
                  active={sorting === 'chronological'}
                />
                <BookingStateItem
                  label="Ohne Datum"
                  onClick={() => updateFilter({ sorting: 'dateless' })}
                  active={sorting === 'dateless'}
                  count={datelessEventCount > 0 ? datelessEventCount : undefined}
                  countWeight="bold"
                />
                <BookingStateItem
                  label="Neue Anfragen"
                  onClick={() => updateFilter({ sorting: 'new_requests' })}
                  active={sorting === 'new_requests'}
                  count={futureEventCount && futureEventCount > 0 ? futureEventCount : undefined}
                  countWeight="bolder"
                />
              </>
            )}
          </>
        )}
        {sorting === 'chronological' && view === 'list' && <DateIntervalSelection />}
        {(sorting !== 'new_requests' || agencyModeActive) && (
          <ListBookingStateSelection hasSettingsScope={hasSettingsScope} />
        )}
        {(sorting !== 'new_requests' || agencyModeActive) && <EventKindSelection hasSettingsScope={hasSettingsScope} />}
        {(sorting !== 'new_requests' || agencyModeActive) && hasGeneralEventsScope && (
          <>
            <Space h="sm" />
            <ThemeText pl={10}>Zugewiesen an</ThemeText>
            <Box p="xs">
              <MultiSelect
                searchable
                clearable
                data={applicationUserItems}
                value={applicationUsersOfInterests.map((applicationUserId) => applicationUserId.toString())}
                onChange={onApplicationUsersChange}
                icon={<IconUser size={16} />}
                variant="filled"
                placeholder="Alle Teammitglieder"
                nothingFound="Keine Teammitglieder gefunden"
                disabled={loadingApplicationUsers}
                itemComponent={ApplicationUserFilterItem}
                zIndex={100}
                shadow="xl"
              />
            </Box>
          </>
        )}
        {sorting !== 'new_requests' && !agencyModeActive && view === 'list' && (
          <>
            <ThemeText mt="xl" pl={10}>
              Optionen
            </ThemeText>
            <Checkbox
              mt="sm"
              ml="md"
              mb="md"
              checked={showAppointments}
              label="Termine anzeigen"
              onChange={() => updateFilter({ showAppointments: !showAppointments })}
            />
          </>
        )}
        {view === 'calendar' && <ExternalCalendarsSelection />}
        {agencyModeActive && <AvailabilitySelection />}
        <Space h="xl" />
      </div>
      {view === 'list' && (
        <div style={{ borderTop: '1px solid #E2E2E2' }}>
          <Center>
            <SegmentedControl
              color="blue"
              value={agencyModeActive ? 'AGENTUR' : 'SOLO'}
              onChange={(val) => {
                executeWithLicenceCheck(LicenceS.Restrictions.AGENCY_MODE, () =>
                  updateFilter({ agencyModeActive: val === 'AGENTUR' }),
                );
              }}
              data={[
                { label: 'Solo', value: 'SOLO' },
                { label: 'Agentur', value: 'AGENTUR' },
              ]}
              sx={{ position: 'absolute', bottom: 20 }}
            />
          </Center>
          <Space h="xl" />
        </div>
      )}
    </div>
  );
};

export default EventListNavBar;
